import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader, Card, Pill, Loader } from "@/components/ui/Primitives";
import {
  Phone, Globe, Mail, MapPin, Star, Wrench, Shield, Calendar, ArrowLeft, BadgeCheck, ExternalLink,
} from "lucide-react";
import VendorContact from "@/components/VendorContact";
import { useAuth } from "@/lib/auth";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function VendorDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    setD(null); setErr(false);
    fetch(`${API}/api/vendors/${encodeURIComponent(id)}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then(setD)
      .catch(() => setErr(true));
  }, [id]);

  if (err) {
    return (
      <div data-testid="vendor-detail-not-found" className="text-center py-20">
        <Wrench className="w-12 h-12 mx-auto text-slate-300 mb-3" />
        <div className="font-display text-xl font-bold text-slate-900">Vendor not found</div>
        <p className="text-sm text-slate-500 mt-2">This profile may have been removed.</p>
        <Link to="/community" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to community vendor directory
        </Link>
      </div>
    );
  }
  if (!d) return <Loader />;
  const v = d.vendor;
  const featured = v.plan === "featured";

  return (
    <div data-testid="vendor-detail-page">
      <Link to="/community" data-testid="vendor-detail-back"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 mb-3">
        <ArrowLeft className="w-4 h-4" /> Vendor directory
      </Link>

      <Card className={`p-7 ${featured ? "ring-1 ring-amber-300 bg-gradient-to-br from-amber-50 via-white to-white" : ""}`}>
        <div className="flex items-start gap-5">
          {v.logo_url ? (
            <img src={v.logo_url} alt={v.name} className="w-20 h-20 rounded-xl object-cover border border-slate-200 flex-none" />
          ) : (
            <div className="w-20 h-20 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-none">
              <Wrench className="w-9 h-9" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl font-extrabold text-slate-900 truncate">{v.name}</h1>
              {featured && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 bg-amber-400 text-amber-950 rounded">
                  <Star className="w-3 h-3" /> Featured
                </span>
              )}
              {v.insured && (
                <Pill tone="emerald"><Shield className="w-3 h-3" /> Insured</Pill>
              )}
              {v.wcb_number && <Pill tone="sky">WCB on file</Pill>}
            </div>
            <div className="text-sm text-emerald-700 font-semibold mt-1 inline-flex items-center gap-1.5">
              <Wrench className="w-3.5 h-3.5" /> {(v.category || "").replace(/-/g, " ")}
              {v.years_in_business ? <span className="text-slate-500 font-medium">· {v.years_in_business}+ years</span> : null}
            </div>

            <div className="flex flex-wrap gap-1.5 mt-3">
              {(v.provinces || []).map((p) => (
                <span key={p} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded text-[11px] font-bold text-slate-700">
                  <MapPin className="w-3 h-3" /> {p}
                </span>
              ))}
            </div>

            {v.description && (
              <p className="mt-4 text-sm leading-relaxed text-slate-700 max-w-3xl whitespace-pre-line">{v.description}</p>
            )}
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-3 max-w-3xl">
          {v.phone && (
            <a href={`tel:${v.phone}`} data-testid="vendor-phone"
              className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors">
              <Phone className="w-4 h-4 text-emerald-700" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Call</div>
                <div className="text-sm font-semibold text-slate-900 truncate">{v.phone}</div>
              </div>
            </a>
          )}
          {v.email && (
            <a href={`mailto:${v.email}`} data-testid="vendor-email"
              className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors">
              <Mail className="w-4 h-4 text-emerald-700" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Email</div>
                <div className="text-sm font-semibold text-slate-900 truncate">{v.email}</div>
              </div>
            </a>
          )}
          {v.website && (
            <a href={v.website} target="_blank" rel="noreferrer" data-testid="vendor-website"
              className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors">
              <Globe className="w-4 h-4 text-emerald-700" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Website</div>
                <div className="text-sm font-semibold text-slate-900 truncate inline-flex items-center gap-1">{cleanHost(v.website)} <ExternalLink className="w-3 h-3" /></div>
              </div>
            </a>
          )}
          {v.cities && (
            <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
              <MapPin className="w-4 h-4 text-emerald-700" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Service cities</div>
                <div className="text-sm font-semibold text-slate-900">{v.cities}</div>
              </div>
            </div>
          )}
          {v.rate && (
            <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200">
              <Calendar className="w-4 h-4 text-emerald-700" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Rate</div>
                <div className="text-sm font-semibold text-slate-900">{v.rate}</div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {d.related?.length > 0 && (
        <div className="mt-7">
          <h3 className="font-display text-base font-bold text-slate-900 mb-3">More {(v.category || "").replace(/-/g, " ")} vendors</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vendor-related">
            {d.related.map((r) => (
              <Link key={r.id} to={`/vendors/${r.id}`} data-testid={`vendor-related-${r.id}`}
                className="block p-3 rounded-lg border border-slate-200 bg-white hover:border-emerald-300 hover:shadow-sm transition-all">
                <div className="font-semibold text-sm text-slate-900 truncate">{r.name}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {(r.provinces || []).slice(0, 3).join(", ")}{r.rate ? ` · ${r.rate}` : ""}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <VendorContact
        vendorId={d.id}
        vendorName={d.name}
        vendorPhone={d.phone}
        user={user}
      />

      <div className="mt-8 p-5 rounded-xl bg-emerald-50 border border-emerald-100 max-w-3xl">
        <div className="flex items-start gap-3">
          <BadgeCheck className="w-5 h-5 text-emerald-600 flex-none mt-0.5" />
          <div>
            <div className="font-bold text-emerald-900">Run a service business?</div>
            <p className="text-sm text-emerald-900/80 mt-1">Apply to join our vendor roster — 12-month directory listing for CA$149/year, with optional Featured upgrade for visibility.</p>
            <Link to="/vendor-apply" data-testid="vendor-detail-apply-cta"
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
              Apply to be listed
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function cleanHost(u) {
  try { return new URL(u).host.replace(/^www\./, ""); } catch (_) { return u; }
}
